/**
 * Branded report PDF template primitives.
 *
 * Visual language is locked to the customer-facing reference HTML:
 *   • Header: gradient navy → primary-blue banner with the Kloudynet logo on a
 *     white card (left) and a centred title + customer name (right slot is a
 *     plain white "customer name" badge — per-customer logos aren't yet plumbed
 *     into the schema).
 *   • Section banners: full-width blue-gradient bar with white title text.
 *   • KPI cards: white rounded card with a 5px coloured left bar, a big
 *     coloured value, a small grey label, and an optional trend line.
 *   • Severity / risk / status / count pills: pastel pill cells inside tables.
 *   • Alert callouts: pastel background + 5px coloured left border + h4 + body.
 *   • Native chart helpers (drawn directly with jsPDF rect / line) for
 *     vertical bars, horizontal bars, line trends, gauges, and stacked
 *     donut-legend bars — Plotly is not used in PDFs.
 *   • Footer: dark-navy "SECURITY OPERATIONS CENTER" banner on the last page,
 *     plus a thin per-page legal/footer strip with page-N-of-M.
 */
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import kloudynetLogo from '@assets/image_1777160759976.png';

// ── Page geometry (A4 portrait, mm) ────────────────────────────────────────
export const PAGE_W = 210;
export const PAGE_H = 297;
export const MARGIN_X = 14;
export const MARGIN_TOP = 14;
export const MARGIN_BOTTOM = 18;
export const CONTENT_W = PAGE_W - MARGIN_X * 2;

// ── Brand palette (matches the reference HTML CSS variables) ───────────────
type RGB = [number, number, number];
export const COLORS = {
  // HTML CSS vars
  primary: [76, 129, 209] as RGB,        // --primary  #4c81d1
  primaryLight: [90, 155, 212] as RGB,   // gradient end (HTML uses #5a9bd4)
  primaryDark: [44, 62, 80] as RGB,      // --primary-dark #2c3e50
  danger: [220, 53, 69] as RGB,          // --danger   #dc3545
  dangerDark: [200, 35, 51] as RGB,      // gradient end #c82333
  warning: [253, 126, 20] as RGB,        // --warning  #fd7e14
  warningDark: [230, 81, 0] as RGB,      // gradient end #e65100 / #ff6f00
  success: [40, 167, 69] as RGB,         // --success  #28a745
  successDark: [21, 128, 61] as RGB,
  info: [23, 162, 184] as RGB,           // --info     #17a2b8
  yellow: [255, 193, 7] as RGB,          // severity-low base
  statusBlue: [0, 123, 255] as RGB,      // .status-new
  // Risk-pill pastels (matches .risk-badge.{critical,high,moderate,low})
  riskCriticalBg: [254, 226, 226] as RGB,
  riskCriticalFg: [220, 38, 38] as RGB,
  riskHighBg: [255, 237, 213] as RGB,
  riskHighFg: [234, 88, 12] as RGB,
  riskModerateBg: [254, 249, 195] as RGB,
  riskModerateFg: [202, 138, 4] as RGB,
  riskLowBg: [209, 250, 229] as RGB,
  riskLowFg: [5, 150, 105] as RGB,
  riskMinimalBg: [241, 245, 249] as RGB,
  riskMinimalFg: [100, 116, 139] as RGB,
  // Severity pill backgrounds (solid, white text — see HTML .severity-* rules)
  // High = danger, Medium = warning, Low = yellow (with dark text), Info = info
  // Status: new = statusBlue, active = warning, closed = success.
  // Alert-box backgrounds + borders
  calloutCriticalBg: [254, 242, 242] as RGB,
  calloutWarningBg: [255, 251, 235] as RGB,
  calloutSuccessBg: [240, 253, 244] as RGB,
  calloutInfoBg: [239, 246, 255] as RGB,
  // Greys
  navy: [44, 62, 80] as RGB,             // alias for primaryDark
  ink: [51, 51, 51] as RGB,              // body text colour from HTML (#333)
  muted: [102, 102, 102] as RGB,         // .kpi-label / .timestamp grey (#666)
  border: [229, 229, 229] as RGB,        // 1px solid #e5e5e5
  rule: [238, 238, 238] as RGB,          // td border-bottom (#eee)
  panelBg: [249, 249, 249] as RGB,       // analysis-card grey (#f9f9f9)
  zebra: [248, 250, 252] as RGB,         // tr:hover (#f8fafc)
  white: [255, 255, 255] as RGB,
};

// ── Logo cache ─────────────────────────────────────────────────────────────
let logoDataUrl: string | null = null;
let logoLoaded = false;

/** Pre-fetch the Kloudynet logo so the cover header can include it. Failure
 *  is silent — the cover header degrades to a text-only treatment. */
export async function preloadPdfLogo(): Promise<void> {
  if (logoLoaded) return;
  logoLoaded = true;
  try {
    const resp = await fetch(kloudynetLogo);
    if (!resp.ok) return;
    const blob = await resp.blob();
    if (!blob.type.startsWith('image/')) return;
    logoDataUrl = await new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result));
      r.onerror = () => reject(r.error);
      r.readAsDataURL(blob);
    });
  } catch {
    logoDataUrl = null;
  }
}

// ── Number / time helpers ──────────────────────────────────────────────────
const NUM_FMT = new Intl.NumberFormat('en-US');

export function fmtNum(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return ' - ';
  return NUM_FMT.format(n);
}

export function fmtPct(p: number | null | undefined, digits = 1): string {
  if (p == null || !Number.isFinite(p)) return ' - ';
  return `${p.toFixed(digits)}%`;
}

export function fmtMinutes(m: number | null | undefined): string {
  if (m == null || !Number.isFinite(m)) return ' - ';
  return `${m.toFixed(2)} min`;
}

export function fmtSecondsCompact(s: number | null | undefined): string {
  if (s == null || !Number.isFinite(s)) return ' - ';
  if (s < 60) return `${Math.round(s)}s`;
  const m = Math.floor(s / 60);
  const r = Math.round(s % 60);
  if (m < 60) return `${m}m ${r}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

export function tzAbbr(tz: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'short',
    }).formatToParts(new Date());
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? tz;
  } catch {
    return tz;
  }
}

export function fmtGeneratedAt(iso: string, tz: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: 'long',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }).formatToParts(d);
    const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
    return `${get('month')} ${get('day')}, ${get('year')} ${get('hour')}:${get('minute')} ${get('dayPeriod').toUpperCase()} ${tzAbbr(tz)}`;
  } catch {
    return iso;
  }
}

// ── Pill cell helpers (autotable cells) ────────────────────────────────────
type PillCell = {
  content: string;
  styles: {
    fillColor: RGB;
    textColor: RGB;
    fontStyle: 'bold';
    halign: 'center';
    valign: 'middle';
  };
};

function pill(label: string, fill: RGB, text: RGB): PillCell {
  return {
    content: label,
    styles: { fillColor: fill, textColor: text, fontStyle: 'bold', halign: 'center', valign: 'middle' },
  };
}

/** Severity pill cell — matches HTML .severity-{high,medium,low,informational}. */
export function severityCell(label: string): PillCell {
  const s = (label || '').toLowerCase();
  if (s.includes('high') || s.includes('critical')) return pill(label, COLORS.danger, COLORS.white);
  if (s.includes('medium')) return pill(label, COLORS.warning, COLORS.white);
  if (s.includes('low')) return pill(label, COLORS.yellow, COLORS.ink);
  if (s.includes('info')) return pill(label, COLORS.info, COLORS.white);
  return pill(label, COLORS.riskMinimalBg, COLORS.riskMinimalFg);
}

/** Risk-level pastel pill cell — matches HTML .risk-badge.{critical,high,moderate,low}. */
export function riskCell(label: string): PillCell {
  const s = (label || '').toUpperCase();
  if (s.includes('CRITICAL')) return pill(label, COLORS.riskCriticalBg, COLORS.riskCriticalFg);
  if (s.includes('HIGH')) return pill(label, COLORS.riskHighBg, COLORS.riskHighFg);
  if (s.includes('MODERATE')) return pill(label, COLORS.riskModerateBg, COLORS.riskModerateFg);
  if (s.includes('LOW')) return pill(label, COLORS.riskLowBg, COLORS.riskLowFg);
  return pill(label, COLORS.riskMinimalBg, COLORS.riskMinimalFg);
}

/** Status pill cell — Active / New / Closed / In Progress. */
export function statusCell(label: string): PillCell {
  const s = (label || '').toLowerCase();
  if (s === 'active' || s.includes('progress')) return pill(label, COLORS.warning, COLORS.white);
  if (s === 'new') return pill(label, COLORS.statusBlue, COLORS.white);
  if (s === 'closed' || s === 'resolved') return pill(label, COLORS.success, COLORS.white);
  return pill(label, COLORS.riskMinimalBg, COLORS.riskMinimalFg);
}

/** Numeric count badge cell (blue / danger). */
export function countBadgeCell(value: number | string, tone: 'primary' | 'danger' = 'primary'): PillCell {
  const fill = tone === 'danger' ? COLORS.danger : COLORS.primary;
  return pill(String(value), fill, COLORS.white);
}

/** Classification pill cell (TP / BP / FP / Undetermined) — matches palette. */
export function classificationCell(label: string): PillCell {
  const s = (label || '').toLowerCase();
  if (s.includes('true')) return pill(label, COLORS.success, COLORS.white);
  if (s.includes('benign')) return pill(label, COLORS.yellow, COLORS.ink);
  if (s.includes('false')) return pill(label, COLORS.danger, COLORS.white);
  if (s.includes('undetermined')) return pill(label, COLORS.info, COLORS.white);
  return pill(label, COLORS.riskMinimalBg, COLORS.riskMinimalFg);
}

// ── Document context ───────────────────────────────────────────────────────
export interface DocMeta {
  /** Big title shown on the cover header. */
  title: string;
  /** Customer / workspace name shown on the right of the header banner. */
  customerName: string;
  /** Reporting period descriptor. */
  periodLabel: string;
  /** Fully-formatted generated timestamp (e.g., "April 27, 2026 10:33 AM MYT"). */
  generatedLabel: string;
}

export interface DocCtx {
  pdf: jsPDF;
  meta: DocMeta;
  /** Tracking cursor for non-table primitives. autoTable manages its own. */
  y: number;
}

export function createReportPdf(meta: DocMeta): DocCtx {
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  pdf.setProperties({
    title: meta.title,
    subject: `${meta.title} for ${meta.customerName}`,
    author: 'Kloudynet SOC',
    creator: 'Kloudynet SOC Dashboard',
  });
  const ctx: DocCtx = { pdf, meta, y: MARGIN_TOP };
  drawHeaderBanner(ctx);
  drawTimestamp(ctx);
  return ctx;
}

// ── Header banner (gradient navy→blue with logo card + customer badge) ─────
/** Approximate a 135° linear-gradient (top-left → bottom-right) by drawing
 *  many thin diagonally-offset stripes — visually indistinguishable from a
 *  Plotly-style CSS gradient at PDF resolution. */
function drawGradientRect(
  pdf: jsPDF,
  x: number, y: number, w: number, h: number,
  start: RGB, end: RGB,
  steps = 40,
): void {
  for (let i = 0; i < steps; i++) {
    const t = i / (steps - 1);
    const r = Math.round(start[0] + (end[0] - start[0]) * t);
    const g = Math.round(start[1] + (end[1] - start[1]) * t);
    const b = Math.round(start[2] + (end[2] - start[2]) * t);
    pdf.setFillColor(r, g, b);
    // Slice horizontally — visual difference vs true diagonal is < 1% at A4.
    pdf.rect(x, y + (h * i) / steps, w, h / steps + 0.05, 'F');
  }
}

function drawHeaderBanner(ctx: DocCtx): void {
  const { pdf, meta } = ctx;
  const bannerH = 30;
  const x = MARGIN_X;
  const y = MARGIN_TOP;
  const w = CONTENT_W;

  // Gradient body
  drawGradientRect(pdf, x, y, w, bannerH, COLORS.primaryDark, COLORS.primary);

  // Left logo card (white pill)
  const logoCardW = 38;
  const logoCardH = 18;
  const logoCardX = x + 5;
  const logoCardY = y + (bannerH - logoCardH) / 2;
  pdf.setFillColor(...COLORS.white);
  pdf.roundedRect(logoCardX, logoCardY, logoCardW, logoCardH, 1.6, 1.6, 'F');
  if (logoDataUrl) {
    try {
      pdf.addImage(
        logoDataUrl, 'PNG',
        logoCardX + 4, logoCardY + 3,
        logoCardW - 8, logoCardH - 6,
        undefined, 'FAST',
      );
    } catch {
      // fall through to text fallback below
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(11);
      pdf.setTextColor(...COLORS.primaryDark);
      pdf.text('KLOUDYNET', logoCardX + logoCardW / 2, logoCardY + logoCardH / 2 + 1, {
        align: 'center', baseline: 'middle',
      });
    }
  } else {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    pdf.setTextColor(...COLORS.primaryDark);
    pdf.text('KLOUDYNET', logoCardX + logoCardW / 2, logoCardY + logoCardH / 2 + 1, {
      align: 'center', baseline: 'middle',
    });
  }

  // Right customer card (white pill with customer name)
  const custCardW = 50;
  const custCardH = 18;
  const custCardX = x + w - custCardW - 5;
  const custCardY = y + (bannerH - custCardH) / 2;
  pdf.setFillColor(...COLORS.white);
  pdf.roundedRect(custCardX, custCardY, custCardW, custCardH, 1.6, 1.6, 'F');
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(9);
  pdf.setTextColor(...COLORS.primaryDark);
  const custLines = pdf.splitTextToSize(meta.customerName, custCardW - 6);
  const custLineH = 4;
  const custTotalH = custLines.length * custLineH;
  let cy = custCardY + (custCardH - custTotalH) / 2 + custLineH / 2 + 0.5;
  for (const line of custLines.slice(0, 3)) {
    pdf.text(String(line), custCardX + custCardW / 2, cy, { align: 'center', baseline: 'middle' });
    cy += custLineH;
  }

  // Centered title + customer subtitle in white -- auto-shrink long titles so
  // they never overlap the logo/customer cards flanking them.
  const titleX = (logoCardX + logoCardW + custCardX) / 2;
  const titleAvailW = custCardX - (logoCardX + logoCardW) - 6;
  pdf.setFont('helvetica', 'bold');
  let titleSize = 15;
  pdf.setFontSize(titleSize);
  let titleW = (pdf.getStringUnitWidth(meta.title) * titleSize) / pdf.internal.scaleFactor;
  while (titleW > titleAvailW && titleSize > 9) {
    titleSize -= 0.5;
    pdf.setFontSize(titleSize);
    titleW = (pdf.getStringUnitWidth(meta.title) * titleSize) / pdf.internal.scaleFactor;
  }
  pdf.setTextColor(...COLORS.white);
  pdf.text(meta.title, titleX, y + bannerH / 2 - 1.5, { align: 'center', baseline: 'middle' });
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(235, 240, 247);
  pdf.text(meta.customerName, titleX, y + bannerH / 2 + 4, { align: 'center', baseline: 'middle' });

  ctx.y = y + bannerH + 5;
}

function drawTimestamp(ctx: DocCtx): void {
  const { pdf, meta } = ctx;
  pdf.setFont('helvetica', 'italic');
  pdf.setFontSize(8.5);
  pdf.setTextColor(...COLORS.muted);
  pdf.text(
    `Report Period: ${meta.periodLabel}  |  Generated: ${meta.generatedLabel}`,
    PAGE_W / 2, ctx.y + 3,
    { align: 'center' },
  );
  ctx.y += 9;
}

// ── Section banner (full-width blue gradient bar) ──────────────────────────
export type BannerTone = 'primary' | 'danger' | 'orange';

export function drawSectionBanner(ctx: DocCtx, label: string, tone: BannerTone = 'primary'): void {
  ensureSpace(ctx, 16);
  const { pdf } = ctx;
  const h = 9.5;
  const y = ctx.y;
  let start: RGB = COLORS.primary;
  let end: RGB = COLORS.primaryLight;
  if (tone === 'danger') { start = COLORS.danger; end = COLORS.dangerDark; }
  if (tone === 'orange') { start = COLORS.warningDark; end = COLORS.warning; }
  drawGradientRect(pdf, MARGIN_X, y, CONTENT_W, h, start, end);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.setTextColor(...COLORS.white);
  pdf.text(label, MARGIN_X + 4, y + h / 2 + 0.5, { baseline: 'middle' });
  ctx.y = y + h + 4;
}

// ── KPI cards (HTML .kpi-card style) ───────────────────────────────────────
export type KpiTone = 'default' | 'success' | 'warning' | 'danger' | 'info';

export interface KpiTile {
  value: string;
  label: string;
  /** Small line below the label (e.g. "vs prior day: ▲ +37"). Plain string. */
  sublabel?: string;
  /** Optional extra coloured badge under the value (e.g. "EXCELLENT"). */
  badgeText?: string;
  badgeTone?: 'success' | 'warning' | 'danger' | 'info' | 'neutral';
  tone?: KpiTone;
}

function toneColor(tone: KpiTone | undefined): RGB {
  switch (tone) {
    case 'success': return COLORS.success;
    case 'warning': return COLORS.warning;
    case 'danger': return COLORS.danger;
    case 'info': return COLORS.info;
    default: return COLORS.primary;
  }
}

export function drawKpiTiles(ctx: DocCtx, tiles: KpiTile[]): void {
  if (tiles.length === 0) return;

  // Lay out in rows of up to 4 (HTML grid auto-fits at minmax(220px, 1fr) which
  // typically gives 3-4 columns on a A4 page; we cap at 4 per row so 6-card
  // layouts split into 4 + 2 nicely).
  const perRow = tiles.length >= 6 ? 3 : Math.min(tiles.length, 4);
  const rows: KpiTile[][] = [];
  for (let i = 0; i < tiles.length; i += perRow) rows.push(tiles.slice(i, i + perRow));

  for (const row of rows) {
    drawKpiRow(ctx, row, perRow);
  }
}

function drawKpiRow(ctx: DocCtx, row: KpiTile[], cols: number): void {
  const tileH = 26;
  ensureSpace(ctx, tileH + 5);
  const { pdf } = ctx;
  const gap = 4;
  const tileW = (CONTENT_W - gap * (cols - 1)) / cols;
  const y = ctx.y;

  row.forEach((t, i) => {
    const x = MARGIN_X + i * (tileW + gap);
    const accent = toneColor(t.tone);

    // Card body — soft drop-shadow approximation (1px slate halo behind)
    pdf.setFillColor(220, 225, 232);
    pdf.roundedRect(x + 0.4, y + 0.6, tileW, tileH, 2, 2, 'F');
    pdf.setFillColor(...COLORS.white);
    pdf.roundedRect(x, y, tileW, tileH, 2, 2, 'F');
    pdf.setDrawColor(...COLORS.border);
    pdf.setLineWidth(0.15);
    pdf.roundedRect(x, y, tileW, tileH, 2, 2, 'S');

    // 5px coloured left bar
    pdf.setFillColor(...accent);
    pdf.rect(x, y, 1.6, tileH, 'F');

    // Big value (coloured to match accent)
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(20);
    pdf.setTextColor(...accent);
    pdf.text(t.value, x + 5, y + 11);

    // Label
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(...COLORS.muted);
    const labelLines = pdf.splitTextToSize(t.label, tileW - 8);
    let ly = y + 16;
    for (const line of labelLines.slice(0, 2)) {
      pdf.text(String(line), x + 5, ly);
      ly += 3.4;
    }

    // Sublabel (e.g. "vs prior day: ▲ +37")
    if (t.sublabel) {
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(7.5);
      pdf.setTextColor(...COLORS.muted);
      pdf.text(t.sublabel, x + 5, y + tileH - 3);
    }

    // Optional badge (e.g., "EXCELLENT")
    if (t.badgeText) {
      const badgeW = pdf.getStringUnitWidth(t.badgeText) * 7 / pdf.internal.scaleFactor + 4;
      const badgeY = y + 17;
      const badgeX = x + tileW - badgeW - 2;
      let bg = COLORS.riskMinimalBg;
      let fg = COLORS.riskMinimalFg;
      switch (t.badgeTone) {
        case 'success': bg = COLORS.success; fg = COLORS.white; break;
        case 'warning': bg = COLORS.warning; fg = COLORS.white; break;
        case 'danger': bg = COLORS.danger; fg = COLORS.white; break;
        case 'info': bg = COLORS.info; fg = COLORS.white; break;
      }
      pdf.setFillColor(...bg);
      pdf.roundedRect(badgeX, badgeY, badgeW, 4.5, 1, 1, 'F');
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(6);
      pdf.setTextColor(...fg);
      pdf.text(t.badgeText, badgeX + badgeW / 2, badgeY + 3.1, { align: 'center', baseline: 'middle' });
    }
  });

  ctx.y = y + tileH + 6;
}

// ── Callout box (HTML .alert-box) ──────────────────────────────────────────
export type CalloutTone = 'critical' | 'warning' | 'success' | 'info' | 'neutral';

export interface CalloutOpts {
  title: string;
  body: string;
  tone?: CalloutTone;
}

export function drawCallout(ctx: DocCtx, opts: CalloutOpts): void {
  const { pdf } = ctx;
  let bg: RGB = COLORS.calloutInfoBg;
  let border: RGB = COLORS.primary;
  let titleC: RGB = COLORS.primary;
  switch (opts.tone) {
    case 'critical': bg = COLORS.calloutCriticalBg; border = COLORS.danger; titleC = COLORS.danger; break;
    case 'warning':  bg = COLORS.calloutWarningBg;  border = COLORS.warning; titleC = COLORS.warningDark; break;
    case 'success':  bg = COLORS.calloutSuccessBg;  border = COLORS.success; titleC = COLORS.successDark; break;
    case 'neutral':  bg = COLORS.panelBg;           border = COLORS.muted;   titleC = COLORS.ink; break;
    default:         bg = COLORS.calloutInfoBg;     border = COLORS.primary; titleC = COLORS.primary;
  }

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8.5);
  const bodyLines = opts.body
    ? pdf.splitTextToSize(opts.body, CONTENT_W - 10)
    : [];
  const boxH = 7 + bodyLines.length * 4 + (bodyLines.length ? 2 : 0);

  ensureSpace(ctx, boxH + 4);
  const y = ctx.y;

  pdf.setFillColor(...bg);
  pdf.roundedRect(MARGIN_X, y, CONTENT_W, boxH, 2, 2, 'F');
  pdf.setFillColor(...border);
  pdf.rect(MARGIN_X, y, 1.5, boxH, 'F');

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(9.5);
  pdf.setTextColor(...titleC);
  pdf.text(opts.title, MARGIN_X + 5, y + 5);

  if (bodyLines.length) {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8.5);
    pdf.setTextColor(...COLORS.ink);
    let by = y + 10;
    for (const line of bodyLines) {
      pdf.text(String(line), MARGIN_X + 5, by);
      by += 4;
    }
  }

  ctx.y = y + boxH + 4;
}

// ── Insight cards row (HTML .insight-card) ─────────────────────────────────
export interface InsightCard { title: string; body: string }

export function drawInsightCards(ctx: DocCtx, cards: InsightCard[]): void {
  if (cards.length === 0) return;
  const { pdf } = ctx;
  const cols = Math.min(cards.length, 2);
  const rows: InsightCard[][] = [];
  for (let i = 0; i < cards.length; i += cols) rows.push(cards.slice(i, i + cols));

  for (const row of rows) {
    const cardW = (CONTENT_W - 4 * (cols - 1)) / cols;
    // Compute height by longest body
    let maxBodyLines = 0;
    pdf.setFontSize(8.5);
    for (const c of row) {
      const lines = pdf.splitTextToSize(c.body, cardW - 8);
      if (lines.length > maxBodyLines) maxBodyLines = lines.length;
    }
    const cardH = 8 + maxBodyLines * 4 + 4;
    ensureSpace(ctx, cardH + 4);
    const y = ctx.y;

    row.forEach((c, i) => {
      const x = MARGIN_X + i * (cardW + 4);
      pdf.setFillColor(...COLORS.panelBg);
      pdf.roundedRect(x, y, cardW, cardH, 2, 2, 'F');
      pdf.setFillColor(...COLORS.primary);
      pdf.rect(x, y, 1.4, cardH, 'F');

      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(9);
      pdf.setTextColor(...COLORS.primary);
      pdf.text(c.title, x + 4, y + 5.5);

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8.5);
      pdf.setTextColor(...COLORS.ink);
      const lines = pdf.splitTextToSize(c.body, cardW - 8);
      let by = y + 10;
      for (const line of lines) {
        pdf.text(String(line), x + 4, by);
        by += 4;
      }
    });

    ctx.y = y + cardH + 4;
  }
}

// ── Tables ────────────────────────────────────────────────────────────────
export type TableCell = string | number | { content: string; styles?: Record<string, unknown> };

export interface TableSection {
  head: string[];
  body: TableCell[][];
  columnStyles?: Record<number, Record<string, unknown>>;
  /** Optional minimum row height (mm) — useful when rows hold tall pills. */
  minRowHeight?: number;
}

const BASE_STYLES = {
  font: 'helvetica' as const,
  fontSize: 8.5,
  cellPadding: { top: 2.4, right: 3, bottom: 2.4, left: 3 },
  textColor: COLORS.ink,
  lineColor: COLORS.rule,
  lineWidth: 0.1,
  overflow: 'linebreak' as const,
};

const HEAD_STYLES = {
  fillColor: COLORS.primary,
  textColor: COLORS.white,
  fontStyle: 'bold' as const,
  fontSize: 8.5,
  lineColor: COLORS.primary,
  lineWidth: 0.1,
  cellPadding: { top: 3, right: 3, bottom: 3, left: 3 },
  halign: 'left' as const,
};

export function drawTable(ctx: DocCtx, t: TableSection): void {
  const { pdf } = ctx;
  if (ctx.y + 18 > PAGE_H - MARGIN_BOTTOM) {
    pdf.addPage();
    ctx.y = MARGIN_TOP;
  }
  autoTable(pdf, {
    head: [t.head],
    body: t.body as never,
    startY: ctx.y,
    margin: { left: MARGIN_X, right: MARGIN_X, bottom: MARGIN_BOTTOM },
    theme: 'grid',
    styles: { ...BASE_STYLES, minCellHeight: t.minRowHeight },
    headStyles: HEAD_STYLES,
    alternateRowStyles: { fillColor: COLORS.zebra },
    columnStyles: t.columnStyles ?? {},
  });
  const lastY =
    (pdf as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? ctx.y;
  ctx.y = lastY + 5;
}

export interface SideBySideOpts {
  /** Optional centred sub-headings drawn above each table (e.g. "Resolved Incidents by Severity"). */
  leftHeading?: string;
  rightHeading?: string;
  /** Optional panel backgrounds (HTML .analysis-card grey panels). */
  panelTone?: 'panel' | 'success' | 'info' | 'none';
}

export function drawSideBySideTables(
  ctx: DocCtx,
  left: TableSection,
  right: TableSection,
  opts: SideBySideOpts = {},
): void {
  const { pdf } = ctx;
  const gap = 4;
  const halfW = (CONTENT_W - gap) / 2;

  // Pre-measure whether we need a page break.
  ensureSpace(ctx, 36);
  const startY = ctx.y;
  const headingH = (opts.leftHeading || opts.rightHeading) ? 7 : 0;

  // Draw heading text first (no panel yet — we'll wrap with panels after we
  // know the final height).
  if (opts.leftHeading) {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    pdf.setTextColor(...COLORS.primary);
    pdf.text(opts.leftHeading, MARGIN_X + halfW / 2, startY + 5, { align: 'center' });
  }
  if (opts.rightHeading) {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    pdf.setTextColor(...COLORS.primary);
    pdf.text(opts.rightHeading, MARGIN_X + halfW + gap + halfW / 2, startY + 5, { align: 'center' });
  }

  const tableY = startY + headingH;

  autoTable(pdf, {
    head: [left.head],
    body: left.body as never,
    startY: tableY,
    margin: { left: MARGIN_X, right: MARGIN_X + halfW + gap, bottom: MARGIN_BOTTOM },
    tableWidth: halfW,
    theme: 'grid',
    styles: { ...BASE_STYLES, minCellHeight: left.minRowHeight },
    headStyles: HEAD_STYLES,
    alternateRowStyles: { fillColor: COLORS.zebra },
    columnStyles: left.columnStyles ?? {},
  });
  const leftEnd =
    (pdf as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? tableY;

  autoTable(pdf, {
    head: [right.head],
    body: right.body as never,
    startY: tableY,
    margin: { left: MARGIN_X + halfW + gap, right: MARGIN_X, bottom: MARGIN_BOTTOM },
    tableWidth: halfW,
    theme: 'grid',
    styles: { ...BASE_STYLES, minCellHeight: right.minRowHeight },
    headStyles: HEAD_STYLES,
    alternateRowStyles: { fillColor: COLORS.zebra },
    columnStyles: right.columnStyles ?? {},
  });
  const rightEnd =
    (pdf as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? tableY;

  ctx.y = Math.max(leftEnd, rightEnd) + 5;
}

// ── Empty-state hint ───────────────────────────────────────────────────────
export function drawEmptyState(ctx: DocCtx, message: string): void {
  ensureSpace(ctx, 14);
  const { pdf } = ctx;
  pdf.setFont('helvetica', 'italic');
  pdf.setFontSize(9);
  pdf.setTextColor(...COLORS.muted);
  pdf.text(message, MARGIN_X, ctx.y + 5);
  ctx.y += 10;
}

// ── Page-break helper ──────────────────────────────────────────────────────
export function ensureSpace(ctx: DocCtx, neededMm: number): void {
  if (ctx.y + neededMm > PAGE_H - MARGIN_BOTTOM) {
    ctx.pdf.addPage();
    ctx.y = MARGIN_TOP;
  }
}

// ── Native chart helpers ───────────────────────────────────────────────────

/** Vertical bar chart drawn natively (no Plotly). One bar per series.
 *  - Bars share the chart width with even spacing.
 *  - Labels appear under each bar; values appear above (or inside if tall).
 *  - Pass `colors[i]` per bar for category colouring; defaults to brand. */
export interface VBarOpts {
  series: { label: string; value: number; color?: RGB }[];
  height?: number;        // mm; default 38
  title?: string;         // optional centred title above the chart
  yLabel?: string;        // optional y-axis label
  /** Force a y-axis maximum (defaults to max(series.value) * 1.15). */
  yMax?: number;
}

export function drawVBar(ctx: DocCtx, opts: VBarOpts): void {
  const h = opts.height ?? 38;
  const titleH = opts.title ? 5 : 0;
  ensureSpace(ctx, h + titleH + 8);
  const { pdf } = ctx;
  const y0 = ctx.y;
  const w = CONTENT_W;
  const padL = opts.yLabel ? 14 : 10;
  const padR = 4;
  const padT = titleH + 6;
  const padB = 12;

  if (opts.title) {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    pdf.setTextColor(...COLORS.ink);
    pdf.text(opts.title, MARGIN_X + w / 2, y0 + 4, { align: 'center' });
  }

  // Plot area
  const plotX = MARGIN_X + padL;
  const plotY = y0 + padT;
  const plotW = w - padL - padR;
  const plotH = h - padT - padB;
  const max = Math.max(opts.yMax ?? 0, ...opts.series.map((s) => s.value), 1);
  const yMax = opts.yMax ?? max * 1.15;

  // Grid + axis
  pdf.setDrawColor(224, 224, 224);
  pdf.setLineWidth(0.15);
  for (let i = 0; i <= 4; i++) {
    const gy = plotY + plotH - (plotH * i) / 4;
    pdf.line(plotX, gy, plotX + plotW, gy);
  }
  // Y tick labels
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(7);
  pdf.setTextColor(...COLORS.muted);
  for (let i = 0; i <= 4; i++) {
    const v = (yMax * i) / 4;
    const gy = plotY + plotH - (plotH * i) / 4;
    pdf.text(formatTick(v), plotX - 1, gy + 1, { align: 'right' });
  }
  if (opts.yLabel) {
    pdf.setTextColor(...COLORS.muted);
    pdf.setFontSize(7);
    pdf.text(opts.yLabel, MARGIN_X + 1, plotY + plotH / 2, { angle: 90, baseline: 'middle' });
  }

  // Bars
  const n = opts.series.length;
  const bandW = plotW / n;
  const barW = Math.min(bandW * 0.55, 18);
  opts.series.forEach((s, i) => {
    const cx = plotX + bandW * i + bandW / 2;
    const bh = (s.value / yMax) * plotH;
    const bx = cx - barW / 2;
    const by = plotY + plotH - bh;
    pdf.setFillColor(...(s.color ?? COLORS.primary));
    pdf.rect(bx, by, barW, bh, 'F');
    // Value label
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(7.5);
    pdf.setTextColor(...COLORS.ink);
    pdf.text(String(s.value), cx, by - 1.2, { align: 'center' });
    // Category label
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7.5);
    pdf.setTextColor(...COLORS.ink);
    const cap = s.label.length > 18 ? s.label.slice(0, 17) + '…' : s.label;
    pdf.text(cap, cx, plotY + plotH + 4, { align: 'center' });
  });

  ctx.y = y0 + h + 4;
}

/** Horizontal bar chart drawn natively. Best for ranked lists (top-N). */
export interface HBarOpts {
  series: { label: string; value: number; color?: RGB }[];
  rowHeight?: number; // mm per row, default 6
  title?: string;
  /** Optional max for the value axis (defaults to max(series.value) * 1.15). */
  xMax?: number;
}

export function drawHBar(ctx: DocCtx, opts: HBarOpts): void {
  const rowH = opts.rowHeight ?? 6;
  const n = opts.series.length;
  if (n === 0) return;
  const titleH = opts.title ? 6 : 0;
  const totalH = titleH + n * rowH + 4;
  ensureSpace(ctx, totalH + 4);

  const { pdf } = ctx;
  const y0 = ctx.y;
  const w = CONTENT_W;

  if (opts.title) {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    pdf.setTextColor(...COLORS.ink);
    pdf.text(opts.title, MARGIN_X + w / 2, y0 + 4, { align: 'center' });
  }

  // Compute label column width based on the longest label.
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8.5);
  let labelW = 0;
  for (const s of opts.series) {
    const cap = s.label.length > 28 ? s.label.slice(0, 27) + '…' : s.label;
    const widthPt = pdf.getStringUnitWidth(cap) * pdf.getFontSize();
    const widthMm = widthPt / pdf.internal.scaleFactor;
    if (widthMm > labelW) labelW = widthMm;
  }
  labelW = Math.min(Math.max(labelW + 2, 22), 60);

  const trackX = MARGIN_X + labelW + 2;
  const valueColW = 12;
  const trackW = w - labelW - 4 - valueColW;
  const xMax = Math.max(opts.xMax ?? 0, ...opts.series.map((s) => s.value), 1);

  opts.series.forEach((s, i) => {
    const ry = y0 + titleH + i * rowH;
    // Label
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8.5);
    pdf.setTextColor(...COLORS.ink);
    const cap = s.label.length > 28 ? s.label.slice(0, 27) + '…' : s.label;
    pdf.text(cap, MARGIN_X, ry + rowH / 2 + 1, { baseline: 'middle' });
    // Track background
    pdf.setFillColor(...COLORS.zebra);
    pdf.rect(trackX, ry + 1.2, trackW, rowH - 2.4, 'F');
    // Bar
    const bw = Math.max((s.value / xMax) * trackW, s.value > 0 ? 0.6 : 0);
    pdf.setFillColor(...(s.color ?? COLORS.primary));
    pdf.rect(trackX, ry + 1.2, bw, rowH - 2.4, 'F');
    // Value
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(8.5);
    pdf.setTextColor(...COLORS.ink);
    pdf.text(String(s.value), MARGIN_X + w, ry + rowH / 2 + 1, { align: 'right', baseline: 'middle' });
  });

  ctx.y = y0 + totalH;
}

/** Side-by-side grouped horizontal bars (e.g. MoM "Current vs Previous"). */
export interface GroupedHBarOpts {
  series: { label: string; current: number; previous: number }[];
  title?: string;
  rowHeight?: number;
  legendCurrent?: string;
  legendPrevious?: string;
}

export function drawGroupedHBar(ctx: DocCtx, opts: GroupedHBarOpts): void {
  const rowH = opts.rowHeight ?? 9;
  const n = opts.series.length;
  if (n === 0) return;
  const titleH = opts.title ? 6 : 0;
  const legendH = (opts.legendCurrent || opts.legendPrevious) ? 6 : 0;
  const totalH = titleH + legendH + n * rowH + 4;
  ensureSpace(ctx, totalH + 4);

  const { pdf } = ctx;
  const y0 = ctx.y;
  const w = CONTENT_W;

  if (opts.title) {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    pdf.setTextColor(...COLORS.ink);
    pdf.text(opts.title, MARGIN_X + w / 2, y0 + 4, { align: 'center' });
  }

  // Legend
  let legendY = y0 + titleH + 1;
  if (legendH) {
    const cur = opts.legendCurrent ?? 'Current';
    const prev = opts.legendPrevious ?? 'Previous';
    pdf.setFontSize(8);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(...COLORS.ink);
    let lx = MARGIN_X + w / 2 - 30;
    pdf.setFillColor(...COLORS.primary);
    pdf.rect(lx, legendY, 4, 3, 'F');
    pdf.text(cur, lx + 5, legendY + 2.2, { baseline: 'middle' });
    lx += 30;
    pdf.setFillColor(...COLORS.muted);
    pdf.rect(lx, legendY, 4, 3, 'F');
    pdf.text(prev, lx + 5, legendY + 2.2, { baseline: 'middle' });
  }

  // Compute label width
  pdf.setFontSize(8.5);
  let labelW = 0;
  for (const s of opts.series) {
    const cap = s.label.length > 24 ? s.label.slice(0, 23) + '…' : s.label;
    const widthMm = (pdf.getStringUnitWidth(cap) * pdf.getFontSize()) / pdf.internal.scaleFactor;
    if (widthMm > labelW) labelW = widthMm;
  }
  labelW = Math.min(Math.max(labelW + 2, 22), 50);

  const trackX = MARGIN_X + labelW + 2;
  const valueColW = 22;
  const trackW = w - labelW - 4 - valueColW;
  const xMax = Math.max(...opts.series.flatMap((s) => [s.current, s.previous]), 1);

  opts.series.forEach((s, i) => {
    const ry = y0 + titleH + legendH + i * rowH;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8.5);
    pdf.setTextColor(...COLORS.ink);
    const cap = s.label.length > 24 ? s.label.slice(0, 23) + '…' : s.label;
    pdf.text(cap, MARGIN_X, ry + rowH / 2 + 1, { baseline: 'middle' });

    const sub = (rowH - 2) / 2;
    // Current bar
    pdf.setFillColor(...COLORS.zebra);
    pdf.rect(trackX, ry + 1, trackW, sub - 0.4, 'F');
    pdf.setFillColor(...COLORS.primary);
    const cw = Math.max((s.current / xMax) * trackW, s.current > 0 ? 0.6 : 0);
    pdf.rect(trackX, ry + 1, cw, sub - 0.4, 'F');
    // Previous bar
    pdf.setFillColor(...COLORS.zebra);
    pdf.rect(trackX, ry + 1 + sub, trackW, sub - 0.4, 'F');
    pdf.setFillColor(...COLORS.muted);
    const pw = Math.max((s.previous / xMax) * trackW, s.previous > 0 ? 0.6 : 0);
    pdf.rect(trackX, ry + 1 + sub, pw, sub - 0.4, 'F');

    // Value text "curr / prev"
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(8);
    pdf.setTextColor(...COLORS.ink);
    pdf.text(`${s.current} / ${s.previous}`, MARGIN_X + w, ry + rowH / 2 + 1, {
      align: 'right', baseline: 'middle',
    });
  });

  ctx.y = y0 + totalH;
}

/** Line trend: small multi-series-friendly line chart. Draws a single line by
 *  default; pass multiple series to overlay them (each gets its own colour). */
export interface LineTrendOpts {
  /** X-axis category labels (one per data point). */
  categories: string[];
  series: { label: string; values: number[]; color?: RGB }[];
  height?: number;
  title?: string;
  showLegend?: boolean;
}

export function drawLineTrend(ctx: DocCtx, opts: LineTrendOpts): void {
  const h = opts.height ?? 50;
  const titleH = opts.title ? 5 : 0;
  const legendH = opts.showLegend ? 5 : 0;
  ensureSpace(ctx, h + titleH + legendH + 6);
  const { pdf } = ctx;
  const y0 = ctx.y;
  const w = CONTENT_W;

  if (opts.title) {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    pdf.setTextColor(...COLORS.ink);
    pdf.text(opts.title, MARGIN_X + w / 2, y0 + 4, { align: 'center' });
  }

  const padL = 12;
  const padR = 4;
  const padT = titleH + 4;
  const padB = 8 + legendH;
  const plotX = MARGIN_X + padL;
  const plotY = y0 + padT;
  const plotW = w - padL - padR;
  const plotH = h - padT - padB;
  const all = opts.series.flatMap((s) => s.values);
  const max = Math.max(...all, 1);
  const yMax = max * 1.15;

  // Grid
  pdf.setDrawColor(224, 224, 224);
  pdf.setLineWidth(0.15);
  for (let i = 0; i <= 4; i++) {
    const gy = plotY + plotH - (plotH * i) / 4;
    pdf.line(plotX, gy, plotX + plotW, gy);
    pdf.setFontSize(6.5);
    pdf.setTextColor(...COLORS.muted);
    pdf.text(formatTick((yMax * i) / 4), plotX - 1, gy + 1, { align: 'right' });
  }

  // X tick labels (subset to avoid overlap)
  const n = opts.categories.length;
  const xStep = plotW / Math.max(n - 1, 1);
  const tickEvery = Math.max(1, Math.ceil(n / 8));
  pdf.setFontSize(6.5);
  pdf.setTextColor(...COLORS.muted);
  opts.categories.forEach((c, i) => {
    if (i % tickEvery !== 0 && i !== n - 1) return;
    pdf.text(c, plotX + i * xStep, plotY + plotH + 4, { align: 'center' });
  });

  // Lines
  opts.series.forEach((s) => {
    const color = s.color ?? COLORS.primary;
    pdf.setDrawColor(...color);
    pdf.setLineWidth(0.5);
    let prev: { x: number; y: number } | null = null;
    s.values.forEach((v, i) => {
      const px = plotX + i * xStep;
      const py = plotY + plotH - (v / yMax) * plotH;
      if (prev) pdf.line(prev.x, prev.y, px, py);
      prev = { x: px, y: py };
    });
    // Dots
    pdf.setFillColor(...color);
    s.values.forEach((v, i) => {
      const px = plotX + i * xStep;
      const py = plotY + plotH - (v / yMax) * plotH;
      pdf.circle(px, py, 0.7, 'F');
    });
  });

  // Legend
  if (opts.showLegend && opts.series.length > 1) {
    let lx = MARGIN_X + 4;
    const ly = y0 + h - 3;
    pdf.setFontSize(7);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(...COLORS.ink);
    for (const s of opts.series) {
      const c = s.color ?? COLORS.primary;
      pdf.setFillColor(...c);
      pdf.rect(lx, ly - 2.4, 4, 2.4, 'F');
      pdf.text(s.label, lx + 5, ly - 0.6, { baseline: 'middle' });
      lx += 4 + 2 + (pdf.getStringUnitWidth(s.label) * 7) / pdf.internal.scaleFactor + 6;
    }
  }

  ctx.y = y0 + h + 3;
}

/** Horizontal "thermometer" gauge — value bar with optional target marker.
 *  Used in place of Plotly half-circle gauges for Triage / High-Sev TTT.
 *  When `value` is null/undefined/non-finite the gauge renders an empty bar
 *  with a "No data" label — never silently coerced to 0. */
export interface GaugeBarOpts {
  label: string;
  /** Numeric value (0..max). `null`/`undefined`/NaN → renders "No data". */
  value: number | null | undefined;
  max: number;
  /** Optional target line (rendered as a vertical dashed marker). */
  target?: number;
  /** Format function for value/target labels. */
  format?: (v: number) => string;
  /** Override bar colour; defaults to a green→amber→red ramp by ratio. */
  color?: RGB;
  /** Optional small status line below the bar (e.g. "EXCELLENT"). */
  status?: { label: string; tone: 'success' | 'warning' | 'danger' | 'info' };
}

export function drawGaugeBar(ctx: DocCtx, opts: GaugeBarOpts): void {
  ensureSpace(ctx, 24);
  const { pdf } = ctx;
  const w = CONTENT_W;
  const y0 = ctx.y;
  const fmt = opts.format ?? ((v) => String(v));
  const hasValue = opts.value != null && Number.isFinite(opts.value);
  const numericValue = hasValue ? (opts.value as number) : 0;

  // Title row: label (left) + value/max (right)
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(9.5);
  pdf.setTextColor(...COLORS.ink);
  pdf.text(opts.label, MARGIN_X, y0 + 3.5);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  const ratio = hasValue ? Math.min(Math.max(numericValue / opts.max, 0), 1) : 0;
  const valueColor = hasValue ? (opts.color ?? gaugeColor(ratio)) : COLORS.muted;
  pdf.setTextColor(...valueColor);
  pdf.text(hasValue ? fmt(numericValue) : 'No data', MARGIN_X + w, y0 + 3.5, { align: 'right' });

  // Bar — track always; filled segment only when there's data (so a missing
  // KPI renders as an empty thermometer rather than a misleading 0-fill sliver).
  const barY = y0 + 6;
  const barH = 5;
  pdf.setFillColor(...COLORS.zebra);
  pdf.roundedRect(MARGIN_X, barY, w, barH, 1.2, 1.2, 'F');
  if (hasValue && ratio > 0) {
    const fillW = Math.max(ratio * w, 0.6);
    pdf.setFillColor(...valueColor);
    pdf.roundedRect(MARGIN_X, barY, fillW, barH, 1.2, 1.2, 'F');
  }

  // Optional target marker
  if (opts.target != null && Number.isFinite(opts.target)) {
    const tx = MARGIN_X + Math.min(opts.target / opts.max, 1) * w;
    pdf.setDrawColor(...COLORS.primaryDark);
    pdf.setLineWidth(0.5);
    pdf.line(tx, barY - 1, tx, barY + barH + 1);
    pdf.setFontSize(6.5);
    pdf.setTextColor(...COLORS.primaryDark);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Target: ${fmt(opts.target)}`, tx, barY + barH + 4, { align: 'center' });
  }

  // Status pill
  if (opts.status) {
    const fill =
      opts.status.tone === 'success' ? COLORS.success :
      opts.status.tone === 'warning' ? COLORS.warning :
      opts.status.tone === 'danger'  ? COLORS.danger  : COLORS.info;
    const tx = MARGIN_X;
    const ty = barY + barH + 6;
    const tw = (pdf.getStringUnitWidth(opts.status.label) * 7) / pdf.internal.scaleFactor + 4;
    pdf.setFillColor(...fill);
    pdf.roundedRect(tx, ty - 3.2, tw, 4.4, 1, 1, 'F');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(7);
    pdf.setTextColor(...COLORS.white);
    pdf.text(opts.status.label, tx + tw / 2, ty - 0.8, { align: 'center', baseline: 'middle' });
    ctx.y = ty + 5;
  } else {
    ctx.y = barY + barH + 5;
  }
}

function gaugeColor(ratio: number): RGB {
  if (ratio < 0.34) return COLORS.success;
  if (ratio < 0.67) return COLORS.warning;
  return COLORS.danger;
}

/** Horizontal stacked bar with legend chips — replaces a Plotly donut for the
 *  "split a total into N coloured slices" use-case. Compact and prints well. */
export interface DonutLegendOpts {
  slices: { label: string; value: number; color: RGB }[];
  /** Optional title above the bar. */
  title?: string;
  /** Bar height in mm (default 5). */
  height?: number;
}

export function drawDonutLegend(ctx: DocCtx, opts: DonutLegendOpts): void {
  const total = opts.slices.reduce((acc, s) => acc + s.value, 0);
  if (total <= 0) {
    drawEmptyState(ctx, 'No data to chart');
    return;
  }
  const titleH = opts.title ? 5 : 0;
  const barH = opts.height ?? 5;
  const legendRows = Math.ceil(opts.slices.length / 2);
  const totalH = titleH + barH + 3 + legendRows * 4 + 2;
  ensureSpace(ctx, totalH + 3);

  const { pdf } = ctx;
  const y0 = ctx.y;
  const w = CONTENT_W;

  if (opts.title) {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    pdf.setTextColor(...COLORS.ink);
    pdf.text(opts.title, MARGIN_X + w / 2, y0 + 4, { align: 'center' });
  }

  // Stacked bar
  let cx = MARGIN_X;
  const barY = y0 + titleH + 1;
  for (const s of opts.slices) {
    const sw = (s.value / total) * w;
    pdf.setFillColor(...s.color);
    pdf.rect(cx, barY, sw, barH, 'F');
    cx += sw;
  }
  pdf.setDrawColor(...COLORS.border);
  pdf.setLineWidth(0.15);
  pdf.rect(MARGIN_X, barY, w, barH, 'S');

  // Legend (2 columns)
  const colW = w / 2;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  pdf.setTextColor(...COLORS.ink);
  opts.slices.forEach((s, i) => {
    const col = i % 2;
    const row = Math.floor(i / 2);
    const lx = MARGIN_X + col * colW;
    const ly = barY + barH + 4 + row * 4;
    pdf.setFillColor(...s.color);
    pdf.rect(lx, ly - 2.4, 3, 2.4, 'F');
    const pct = ((s.value / total) * 100).toFixed(1);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`${s.label}: `, lx + 4, ly - 0.6, { baseline: 'middle' });
    pdf.setFont('helvetica', 'bold');
    pdf.text(`${s.value} (${pct}%)`, lx + 4 + (pdf.getStringUnitWidth(`${s.label}: `) * 8) / pdf.internal.scaleFactor, ly - 0.6, { baseline: 'middle' });
  });

  ctx.y = y0 + totalH;
}

function formatTick(v: number): string {
  if (v >= 1000) return `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k`;
  if (Number.isInteger(v)) return String(v);
  return v.toFixed(1);
}

// ── Closing footer banner (dark navy, last page only) ──────────────────────
export function drawClosingFooter(ctx: DocCtx): void {
  const { pdf, meta } = ctx;
  const bannerH = 34;

  // If not enough room, flow to new page first.
  if (ctx.y + bannerH > PAGE_H - MARGIN_BOTTOM - 4) {
    pdf.addPage();
    ctx.y = MARGIN_TOP;
  }
  const y = ctx.y + 4;

  pdf.setFillColor(...COLORS.primaryDark);
  pdf.roundedRect(MARGIN_X, y, CONTENT_W, bannerH, 2, 2, 'F');

  // Optional logo at top of banner
  if (logoDataUrl) {
    try {
      pdf.addImage(logoDataUrl, 'PNG', PAGE_W / 2 - 12, y + 2.5, 24, 7, undefined, 'FAST');
    } catch { /* ignore */ }
  }

  // Copyright caption directly under the logo.
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(6.5);
  pdf.setTextColor(190, 205, 230);
  pdf.text(`Property of Kloudynet © ${new Date().getFullYear()}`, PAGE_W / 2, y + 11, { align: 'center' });

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.setTextColor(...COLORS.white);
  pdf.text('SECURITY OPERATIONS CENTER', PAGE_W / 2, y + 17.5, { align: 'center' });

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8.5);
  pdf.setTextColor(...COLORS.white);
  pdf.text('Powered by Kloudynet Advanced Threat Detection', PAGE_W / 2, y + 22.5, { align: 'center' });

  pdf.setFontSize(7.5);
  pdf.setTextColor(220, 230, 245);
  pdf.text(`Report generated on ${meta.generatedLabel}`, PAGE_W / 2, y + 27, { align: 'center' });
  pdf.text(
    'This report contains confidential security information   •   Distribution restricted to authorized personnel',
    PAGE_W / 2,
    y + 31,
    { align: 'center' },
  );

  ctx.y = y + bannerH + 2;
}

/** Walk every page and stamp a slim footer with the generated stamp + page x/y.
 *  Call AFTER all content has been drawn (so the page count is final). */
export function finalizeFooters(ctx: DocCtx): void {
  const { pdf, meta } = ctx;
  const total = pdf.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    pdf.setPage(i);
    const y = PAGE_H - 8;
    pdf.setDrawColor(...COLORS.border);
    pdf.setLineWidth(0.15);
    pdf.line(MARGIN_X, y - 3, PAGE_W - MARGIN_X, y - 3);

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7);
    pdf.setTextColor(...COLORS.muted);
    pdf.text('Confidential - Distribution restricted to authorized personnel', MARGIN_X, y);
    pdf.text(`Generated ${meta.generatedLabel}`, PAGE_W / 2, y, { align: 'center' });
    pdf.text(`Page ${i} of ${total}`, PAGE_W - MARGIN_X, y, { align: 'right' });
  }
}

export function savePdf(ctx: DocCtx, baseFileName: string): string {
  const stamp = new Date().toISOString().slice(0, 10);
  const fileName = `${baseFileName}-${stamp}.pdf`;
  ctx.pdf.save(fileName);
  return fileName;
}

// ── Centralized chart-color helpers ──────────────────────────────────────
// These mappings must stay aligned with the React report views
// (WeeklyReportView.tsx / MonthlyReportView.tsx / ReportView.tsx) — the
// printable PDFs and the on-screen reports must use the same color semantics.

/** Map a severity label (case-insensitive) to its chart fill color.
 *  critical/high → danger-red, medium → warning-orange, info → info-cyan,
 *  anything else → muted-grey. The Low color differs by report type to mirror
 *  the React views: Daily uses yellow (matches `SEV_COLOR.Low = C.yellow`),
 *  Weekly/Monthly use success-green (matches `SEVERITY_COLORS.Low = C.low`
 *  which resolves to `#28a745`). Pass `lowTone: 'success'` from the weekly /
 *  monthly exporters; default keeps daily parity. */
export function severityChartColor(
  severity: string,
  opts: { lowTone?: 'yellow' | 'success' } = {},
): RGB {
  const t = (severity || '').toLowerCase();
  if (t.includes('critical') || t.includes('high')) return COLORS.danger;
  if (t.includes('medium')) return COLORS.warning;
  if (t.includes('low')) return opts.lowTone === 'success' ? COLORS.success : COLORS.yellow;
  if (t.includes('info')) return COLORS.info;
  return COLORS.muted;
}

/** Map an incident-classification label to its chart fill color. Mirrors the
 *  React view palette: True Positive = danger (real threat), Benign Positive
 *  = success (legitimate activity), False Positive = warning (mis-fire),
 *  Undetermined = info, anything else = primary blue. */
export function classificationChartColor(classification: string): RGB {
  const t = (classification || '').toLowerCase().replace(/\s|_/g, '');
  if (t.includes('truepositive') || t.startsWith('true')) return COLORS.danger;
  if (t.includes('benignpositive') || t.startsWith('benign')) return COLORS.success;
  if (t.includes('falsepositive') || t.startsWith('false')) return COLORS.warning;
  if (t.includes('undetermined')) return COLORS.info;
  return COLORS.primary;
}

/** Bucket a raw event count into a coarse risk-pill label. Used by failed
 *  sign-in / malicious-attempt / asset / MITRE tables in place of the
 *  hard-coded "Critical" pill from the reference HTML samples. */
export function volumeRiskBucket(count: number): string {
  if (count >= 100000) return 'CRITICAL';
  if (count >= 10000) return 'HIGH';
  if (count >= 1000) return 'MODERATE';
  if (count >= 100) return 'LOW';
  return 'MINIMAL';
}

/** Stable A→B severity ordering: Critical, High, Medium, Low, Info, other. */
export function severityRank(severity: string): number {
  const t = (severity || '').toLowerCase();
  if (t.includes('critical')) return 0;
  if (t.includes('high')) return 1;
  if (t.includes('medium')) return 2;
  if (t.includes('low')) return 3;
  if (t.includes('info')) return 4;
  return 5;
}

/** Title-case helper for severity / status labels. */
export function prettyCase(s: string): string {
  if (!s) return 'Unknown';
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}
